import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Upload, Trash2, X, FileUp, Truck, AlertTriangle, Fuel, TrendingUp, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n, cur = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n || 0);

const COST_CATEGORIES = ["Yakıt", "Otoyol", "Konaklama", "Yemek", "Bakım", "Sigorta", "Vinyet", "Diğer"];

function Modal({ title, children, onClose, wide }) {
  return <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop">
    <div className={`modal ${wide ? "modal-wide" : ""}`} onClick={e => e.stopPropagation()}>
      <div className="modal-head"><h3>{title}</h3><button onClick={onClose} data-testid="modal-close"><X size={18} /></button></div>
      {children}
    </div>
  </div>;
}
function Field({ label, children }) { return <label className="field"><span>{label}</span>{children}</label>; }

export default function OperationsPage({ company, companies, user }) {
  const canEdit = user?.role !== "viewer";
  const [trips, setTrips] = useState([]);
  const [dash, setDash] = useState(null);
  const [mondayStatus, setMondayStatus] = useState(null);
  const [showTripForm, setShowTripForm] = useState(false);
  const [showCosts, setShowCosts] = useState(null);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [filter, setFilter] = useState("all");
  const [importMsg, setImportMsg] = useState("");

  const load = async () => {
    const [t, d, m] = await Promise.all([
      axios.get(`${API}/trips`, { params: { company_id: company, status: filter } }),
      axios.get(`${API}/operations-dashboard`),
      axios.get(`${API}/monday/status`),
    ]);
    setTrips(t.data); setDash(d.data); setMondayStatus(m.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company, filter]);

  const importCosts = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append("file", file);
    setImportMsg("Masraflar yükleniyor...");
    try {
      const { data } = await axios.post(`${API}/trips/import-costs`, form);
      setImportMsg(data.message);
      load();
    } catch (e) { setImportMsg(e.response?.data?.detail || "Hata"); }
    ev.target.value = "";
  };

  const removeTrip = async (t) => {
    if (!window.confirm(`${t.trip_no} seferi ve masrafları silinsin mi?`)) return;
    await axios.delete(`${API}/trips/${t.id}`); load();
  };

  return <div className="page">
    <section className="page-toolbar">
      <div>
        <p className="eyebrow">OPERASYON — SEFERLER</p>
        <h2>Lojistik Gelir / Gider</h2>
        <p className="muted">Her sefer için araç bazlı masraf dağılımı. Eurowag / Monday.com entegrasyonu.</p>
      </div>
      {canEdit && <div className="toolbar-actions">
        <label className="outline-btn" data-testid="ops-import-costs">
          <FileUp size={16} /> Masraf CSV aktar
          <input type="file" accept=".csv,.xlsx,.xls" onChange={importCosts} hidden data-testid="ops-import-input" />
        </label>
        <button className="primary-btn" data-testid="ops-new-trip-btn" onClick={() => setShowTripForm(true)}><Plus size={17} /> Yeni sefer</button>
      </div>}
    </section>

    {importMsg && <div className="panel" style={{ padding: 12, marginBottom: 12, background: "#eff6ff", color: "#1e40af" }} data-testid="ops-import-msg">{importMsg}</div>}

    {/* Monday status */}
    {mondayStatus && <section className="panel" style={{ marginBottom: 12, padding: 12, display: "flex", alignItems: "center", gap: 12 }}>
      <Truck size={16} color={mondayStatus.configured ? "#16a34a" : "#f59e0b"} />
      <div style={{ fontSize: 13 }} className="muted"><strong>Monday.com:</strong> {mondayStatus.message}</div>
    </section>}

    {/* Dashboard summary */}
    {dash && <section className="metric-grid" data-testid="ops-dashboard">
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label"><DollarSign size={13} /> Toplam Gelir</span></div>
        <div className="metric-value">{money(dash.total_revenue_try)}</div>
      </div>
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label"><Fuel size={13} /> Toplam Gider</span></div>
        <div className="metric-value">{money(dash.total_cost_try)}</div>
      </div>
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label"><TrendingUp size={13} /> Net Kâr</span></div>
        <div className="metric-value" style={{ color: dash.profit_try >= 0 ? "#16a34a" : "#dc2626" }}>{money(dash.profit_try)}</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Marj: %{dash.margin_pct}</div>
      </div>
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label"><Truck size={13} /> Aktif Seferler</span></div>
        <div className="metric-value">{dash.active_trips}/{dash.total_trips}</div>
        {dash.unassigned_costs > 0 && <button onClick={() => setShowUnassigned(true)} data-testid="ops-unassigned-btn" style={{ marginTop: 8, color: "#dc2626", background: "none", border: "none", padding: 0, fontSize: 12, cursor: "pointer", textAlign: "left" }}>
          <AlertTriangle size={11} /> {dash.unassigned_costs} atanmamış masraf
        </button>}
      </div>
    </section>}

    <section className="panel full-panel">
      <div style={{ display: "flex", gap: 8, padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>
        {["all", "Devam ediyor", "Tamamlandı", "İptal"].map(s => (
          <button key={s} data-testid={`ops-filter-${s}`} onClick={() => setFilter(s)}
            style={{
              padding: "6px 12px", border: "1px solid #e5e7eb", borderRadius: 6, background: filter === s ? "#1e40af" : "white",
              color: filter === s ? "white" : "#1f2937", cursor: "pointer", fontSize: 13,
            }}>{s === "all" ? "Tümü" : s}</button>
        ))}
      </div>
      <div className="table-scroll"><table>
        <thead><tr>
          <th>SEFER</th><th>PLAKA</th><th>GÜZERGAH</th><th>TARİH</th>
          <th>GELİR</th><th>GİDER</th><th>KAR</th><th>MARJ</th><th>DURUM</th><th>İŞLEM</th>
        </tr></thead>
        <tbody>
          {trips.length === 0 && <tr><td colSpan="10" style={{ textAlign: "center", padding: 24 }} className="muted">Henüz sefer yok</td></tr>}
          {trips.map(t => <tr key={t.id} data-testid={`trip-row-${t.id}`}>
            <td><strong>{t.trip_no}</strong>{t.driver_name && <span>{t.driver_name}</span>}</td>
            <td style={{ fontFamily: "monospace" }}>{t.vehicle_plate}</td>
            <td>{t.route_from} → {t.route_to}{t.distance_km ? <span>{t.distance_km} km</span> : null}</td>
            <td>{t.start_date}{t.end_date ? ` → ${t.end_date}` : ""}</td>
            <td>{money(t.revenue_try)}</td>
            <td>{money(t.cost_try)}{t.cost_count === 0 && <AlertTriangle size={12} color="#f59e0b" style={{ marginLeft: 4, display: "inline" }} />}</td>
            <td style={{ color: t.profit_try >= 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>{money(t.profit_try)}</td>
            <td>%{t.margin_pct}</td>
            <td><span className={`type ${t.status === "Tamamlandı" ? "income" : t.status === "İptal" ? "expense" : ""}`}>{t.status}</span></td>
            <td>
              <button className="outline-btn" style={{ padding: "4px 10px", fontSize: 12 }} data-testid={`trip-costs-${t.id}`} onClick={() => setShowCosts(t)}>Masraflar ({t.cost_count})</button>
              {user?.role === "admin" && <button className="icon-btn" style={{ padding: 4, marginLeft: 4 }} data-testid={`trip-del-${t.id}`} onClick={() => removeTrip(t)}><Trash2 size={13} /></button>}
            </td>
          </tr>)}
        </tbody>
      </table></div>
    </section>

    {showTripForm && <TripForm companies={companies} onClose={() => setShowTripForm(false)} onDone={() => { setShowTripForm(false); load(); }} />}
    {showCosts && <TripCostsModal trip={showCosts} canEdit={canEdit} onClose={() => setShowCosts(null)} onChange={load} />}
    {showUnassigned && <UnassignedCostsModal trips={trips} onClose={() => setShowUnassigned(false)} onChange={load} />}
  </div>;
}

function TripForm({ companies, onClose, onDone }) {
  const [f, setF] = useState({
    trip_no: "", vehicle_plate: "", driver_name: "", route_from: "", route_to: "",
    start_date: new Date().toISOString().slice(0, 10), end_date: "",
    revenue: 0, currency: "TRY", distance_km: 0, status: "Devam ediyor",
    company_id: companies?.[0]?.id || "", notes: "",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const p = { ...f, revenue: parseFloat(f.revenue || 0), distance_km: parseFloat(f.distance_km || 0) };
      if (!p.end_date) delete p.end_date;
      await axios.post(`${API}/trips`, p); onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };
  return <Modal onClose={onClose} title="Yeni sefer">
    <form onSubmit={submit} className="form">
      <div className="row2">
        <Field label="Sefer no"><input data-testid="trip-form-no" value={f.trip_no} onChange={e => setF({ ...f, trip_no: e.target.value })} required placeholder="SF-10050" /></Field>
        <Field label="Plaka"><input data-testid="trip-form-plate" value={f.vehicle_plate} onChange={e => setF({ ...f, vehicle_plate: e.target.value.toUpperCase() })} required placeholder="34 ABC 1234" /></Field>
      </div>
      <Field label="Şoför"><input data-testid="trip-form-driver" value={f.driver_name} onChange={e => setF({ ...f, driver_name: e.target.value })} /></Field>
      <div className="row2">
        <Field label="Çıkış"><input data-testid="trip-form-from" value={f.route_from} onChange={e => setF({ ...f, route_from: e.target.value })} required placeholder="İstanbul" /></Field>
        <Field label="Varış"><input data-testid="trip-form-to" value={f.route_to} onChange={e => setF({ ...f, route_to: e.target.value })} required placeholder="Berlin" /></Field>
      </div>
      <div className="row2">
        <Field label="Başlangıç"><input data-testid="trip-form-start" type="date" value={f.start_date} onChange={e => setF({ ...f, start_date: e.target.value })} required /></Field>
        <Field label="Bitiş (opsiyonel)"><input data-testid="trip-form-end" type="date" value={f.end_date} onChange={e => setF({ ...f, end_date: e.target.value })} /></Field>
      </div>
      <div className="row2">
        <Field label="Mesafe (km)"><input data-testid="trip-form-km" type="number" value={f.distance_km} onChange={e => setF({ ...f, distance_km: e.target.value })} /></Field>
        <Field label="Durum"><select data-testid="trip-form-status" value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
          <option>Devam ediyor</option><option>Tamamlandı</option><option>İptal</option>
        </select></Field>
      </div>
      <div className="row2">
        <Field label="Gelir"><input data-testid="trip-form-revenue" type="number" step="0.01" value={f.revenue} onChange={e => setF({ ...f, revenue: e.target.value })} /></Field>
        <Field label="Para birimi"><select data-testid="trip-form-currency" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })}>
          {["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}
        </select></Field>
      </div>
      <Field label="Şirket"><select data-testid="trip-form-company" value={f.company_id} onChange={e => setF({ ...f, company_id: e.target.value })}>
        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select></Field>
      <Field label="Notlar"><textarea data-testid="trip-form-notes" rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></Field>
      {err && <div className="auth-err">{err}</div>}
      <div className="form-actions">
        <button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button>
        <button data-testid="trip-form-submit" className="primary-btn" disabled={busy}>{busy ? "..." : "Kaydet"}</button>
      </div>
    </form>
  </Modal>;
}

function TripCostsModal({ trip, canEdit, onClose, onChange }) {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = async () => {
    const { data } = await axios.get(`${API}/trips/${trip.id}/costs`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const del = async (c) => {
    if (!window.confirm("Silinsin mi?")) return;
    await axios.delete(`${API}/trip-costs/${c.id}`); load(); onChange?.();
  };
  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
  return <Modal onClose={onClose} wide title={`${trip.trip_no} — Masraflar`}>
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 14px" }}>
      <div className="muted">
        {rows.length} kalem · Gelir {money(trip.revenue_try)} · Gider {money(trip.cost_try)} · Kâr <strong style={{ color: trip.profit_try >= 0 ? "#16a34a" : "#dc2626" }}>{money(trip.profit_try)}</strong>
      </div>
      {canEdit && <button className="primary-btn" data-testid="trip-cost-new-btn" onClick={() => setShowForm(true)}><Plus size={15} /> Yeni masraf</button>}
    </div>
    {rows.length === 0 && <div style={{ padding: 20, textAlign: "center", background: "#fef3c7", borderRadius: 6, color: "#92400e", marginBottom: 12 }} data-testid="trip-cost-warning">
      <AlertTriangle size={14} style={{ display: "inline", marginRight: 6 }} />
      Bu sefere henüz masraf girilmedi. Manuel kayıt veya CSV aktarımı gerekiyor.
    </div>}
    <div className="table-scroll"><table>
      <thead><tr><th>TARİH</th><th>KATEGORİ</th><th>AÇIKLAMA</th><th>TUTAR</th><th>KAYNAK</th><th>İŞLEM</th></tr></thead>
      <tbody>
        {rows.map(r => <tr key={r.id} data-testid={`tcost-row-${r.id}`}>
          <td>{r.cost_date}</td>
          <td><span className="company-pill">{r.category}</span></td>
          <td>{r.description || "—"}</td>
          <td>{money(r.amount, r.currency)}</td>
          <td className="muted" style={{ fontSize: 12 }}>{r.source}</td>
          <td>{canEdit && <button className="icon-btn" onClick={() => del(r)} data-testid={`tcost-del-${r.id}`}><Trash2 size={13} /></button>}</td>
        </tr>)}
      </tbody>
    </table></div>
    {showForm && <TripCostForm tripId={trip.id} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); onChange?.(); }} />}
  </Modal>;
}

function TripCostForm({ tripId, onClose, onDone }) {
  const [f, setF] = useState({
    trip_id: tripId, category: "Yakıt", description: "", amount: "", currency: "TRY",
    cost_date: new Date().toISOString().slice(0, 10), source: "manual",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.post(`${API}/trip-costs`, { ...f, amount: parseFloat(f.amount) }); onDone(); }
    catch (ex) { setErr(ex.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };
  return <Modal onClose={onClose} title="Yeni masraf">
    <form onSubmit={submit} className="form">
      <div className="row2">
        <Field label="Kategori"><select data-testid="tcost-form-cat" value={f.category} onChange={e => setF({ ...f, category: e.target.value })}>
          {COST_CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select></Field>
        <Field label="Tarih"><input data-testid="tcost-form-date" type="date" value={f.cost_date} onChange={e => setF({ ...f, cost_date: e.target.value })} required /></Field>
      </div>
      <Field label="Açıklama"><input data-testid="tcost-form-desc" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></Field>
      <div className="row2">
        <Field label="Tutar"><input data-testid="tcost-form-amount" type="number" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} required /></Field>
        <Field label="Para birimi"><select data-testid="tcost-form-currency" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })}>
          {["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}
        </select></Field>
      </div>
      {err && <div className="auth-err">{err}</div>}
      <div className="form-actions">
        <button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button>
        <button data-testid="tcost-form-submit" className="primary-btn" disabled={busy}>{busy ? "..." : "Kaydet"}</button>
      </div>
    </form>
  </Modal>;
}

function UnassignedCostsModal({ trips, onClose, onChange }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const load = async () => {
    const { data } = await axios.get(`${API}/trip-costs/unassigned`);
    setRows(data);
  };
  useEffect(() => { load(); }, []);
  const assign = async (cost) => {
    const tripId = selected[cost.id];
    if (!tripId) return alert("Lütfen bir sefer seçin");
    await axios.patch(`${API}/trip-costs/${cost.id}/assign`, { trip_id: tripId });
    load(); onChange?.();
  };
  return <Modal onClose={onClose} wide title="Atanmamış masraflar">
    <div className="muted" style={{ padding: "8px 0 14px" }}>Plakadan sefer bulunamadığı için atanamayan masrafları manuel eşleştirin.</div>
    <div className="table-scroll"><table>
      <thead><tr><th>TARİH</th><th>PLAKA</th><th>KATEGORİ</th><th>AÇIKLAMA</th><th>TUTAR</th><th>SEFER</th><th>İŞLEM</th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan="7" style={{ textAlign: "center", padding: 16 }} className="muted">Tüm masraflar atanmış ✓</td></tr>}
        {rows.map(r => <tr key={r.id} data-testid={`unassigned-${r.id}`}>
          <td>{r.cost_date}</td>
          <td>{r.vehicle_plate}</td>
          <td>{r.category}</td>
          <td>{r.description}</td>
          <td>{money(r.amount, r.currency)}</td>
          <td><select data-testid={`unassigned-select-${r.id}`} value={selected[r.id] || ""} onChange={e => setSelected({ ...selected, [r.id]: e.target.value })}>
            <option value="">— Seçin —</option>
            {trips.map(t => <option key={t.id} value={t.id}>{t.trip_no} · {t.vehicle_plate}</option>)}
          </select></td>
          <td><button className="outline-btn" onClick={() => assign(r)} data-testid={`unassigned-assign-${r.id}`}>Ata</button></td>
        </tr>)}
      </tbody>
    </table></div>
  </Modal>;
}
